/**
 * Aba "Satélite": a pilha de produtos do GOES-19 e, como subcamadas, o
 * rastreio e a previsão de nuvens (src/tracking). Qualidade/memória e
 * atualização automática ficam num diálogo de configurações, aberto pelo botão
 * de engrenagem no cabeçalho da seção, para a aba não ficar poluída.
 */
import { Gauge, Layers, Palette, Plus, RefreshCw, Settings2 } from 'lucide-react';
import { useState } from 'react';
import { MAX_GOES_LAYERS } from '../config/products';
import { useCatalogStore } from '../goes/catalog';
import { formatRelative, formatTimeBrtLabeled } from '../lib/time';
import { checkNow } from '../loop/autoRefresh';
import { useGoesStore } from '../state/goes';
import { TrackingSection } from '../tracking/TrackingSection';
import { showToast } from '../state/toasts';
import { useNow } from './hooks';
import { LegendaDasImagens } from '../legend/MapLegend';
import { useIsMobile } from './useMediaQuery';
import { LayerCard } from './LayerCard';
import { ProductPicker } from './ProductPicker';
import { QualitySection } from './QualitySection';
import { Badge, Button, Dialog, Hint, IconButton, Section } from './primitives';

type PickerState = { mode: 'add' } | { mode: 'change'; uid: string } | null;

function LayerStack() {
  const mobile = useIsMobile();
  const layers = useGoesStore((s) => s.layers);
  const [picker, setPicker] = useState<PickerState>(null);
  const selectedId = picker?.mode === 'change' ? layers.find((l) => l.uid === picker.uid)?.productId : undefined;

  const onSelect = (productId: string) => {
    const { addLayer, updateLayer } = useGoesStore.getState();
    if (picker?.mode === 'add') addLayer(productId);
    else if (picker?.mode === 'change') updateLayer(picker.uid, { productId });
    setPicker(null);
  };

  // Lista de cima para baixo (como a pilha aparece no mapa).
  const ordered = layers.map((layer, position) => ({ layer, position })).reverse();

  return (
    <>
      <ul className="space-y-2">
        {ordered.map(({ layer, position }) => (
          <LayerCard
            key={layer.uid}
            layer={layer}
            position={position}
            count={layers.length}
            onChangeProduct={() => setPicker({ mode: 'change', uid: layer.uid })}
          />
        ))}
      </ul>
      {/* No celular a legenda não fica sobre o mapa: mora aqui, logo abaixo das imagens que ela explica. */}
      {mobile && layers.length > 0 && (
        <div className="rounded-xl border border-ink-700/70 bg-ink-850/60 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-ink-100">
            <Palette className="size-3.5 text-mata-400" aria-hidden />
            Legenda das cores
          </div>
          <LegendaDasImagens />
        </div>
      )}
      <Button
        icon={Plus}
        className="w-full"
        disabled={layers.length >= MAX_GOES_LAYERS}
        onClick={() => setPicker({ mode: 'add' })}
      >
        {layers.length >= MAX_GOES_LAYERS ? `Máximo de ${MAX_GOES_LAYERS} camadas` : 'Adicionar camada'}
      </Button>
      <ProductPicker
        open={picker !== null}
        title={picker?.mode === 'add' ? 'Adicionar uma camada' : 'Escolha a imagem do satélite'}
        selectedId={selectedId}
        onSelect={onSelect}
        onClose={() => setPicker(null)}
      />
    </>
  );
}

function RefreshSection() {
  const lastCheck = useCatalogStore((s) => s.lastCheck);
  const now = useNow();
  const [busy, setBusy] = useState(false);

  const onCheck = async () => {
    setBusy(true);
    try {
      const found = await checkNow();
      showToast({
        id: 'check',
        kind: found ? 'success' : 'info',
        message:
          found === null
            ? 'Já há uma verificação em andamento.'
            : found
              ? 'Chegaram imagens novas.'
              : 'Nenhuma imagem nova por enquanto.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Hint>
        As imagens novas entram sozinhas: a cada minuto o portal confere se a NOAA publicou um quadro novo (pausa com a
        aba escondida) e baixa o que chegou.
      </Hint>
      <div className="flex items-center justify-between gap-2 text-xs text-ink-300">
        <span>
          {lastCheck
            ? `Última verificação: ${formatTimeBrtLabeled(lastCheck)}, ${formatRelative(lastCheck, now)}`
            : 'Aguardando a primeira resposta da NOAA…'}
        </span>
        <Button size="sm" variant="ghost" icon={RefreshCw} disabled={busy} onClick={() => void onCheck()} className="shrink-0 whitespace-nowrap">
          Verificar agora
        </Button>
      </div>
    </div>
  );
}

/** Configurações das imagens: qualidade/memória e atualização automática. */
function SatelliteSettingsDialog(props: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={props.open} onClose={props.onClose} title="Configurações das imagens">
      <div className="space-y-6">
        <section className="space-y-3" aria-labelledby="cfg-qualidade">
          <h3 id="cfg-qualidade" className="flex items-center gap-2 text-sm font-semibold text-white">
            <Gauge className="size-4 text-mata-400" aria-hidden />
            Qualidade e memória
          </h3>
          <QualitySection />
        </section>
        <section className="space-y-3 border-t border-ink-700/70 pt-5" aria-labelledby="cfg-atualizacao">
          <h3 id="cfg-atualizacao" className="flex items-center gap-2 text-sm font-semibold text-white">
            <RefreshCw className="size-4 text-mata-400" aria-hidden />
            Atualização
          </h3>
          <RefreshSection />
        </section>
      </div>
    </Dialog>
  );
}

export function SatellitePanel() {
  const count = useGoesStore((s) => s.layers.length);
  const [settingsOpen, setSettingsOpen] = useState(false);
  return (
    <div>
      <Section
        title="Imagens do satélite"
        icon={Layers}
        right={
          <div className="flex items-center gap-1">
            <Badge>{`${count}/${MAX_GOES_LAYERS}`}</Badge>
            <IconButton
              icon={Settings2}
              size="sm"
              label="Configurações das imagens (qualidade e atualização)"
              onClick={() => setSettingsOpen(true)}
              className="pointer-coarse:size-11"
            />
          </div>
        }
      >
        <LayerStack />
      </Section>
      <TrackingSection />
      <SatelliteSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
